/**
 * Alle Nederlandse teksten van het opnamescherm en het energielabelscherm.
 * Jij-vorm, geen uitroeptekens, geen marketingtaal. Labels zijn zelfstandige
 * naamwoorden, knoppen zijn werkwoorden (principe 10).
 */
export const surveyCopy = {
    header: {
        /* Kort naast de pijl terug; voluit staan ze onder `load`. */
        backToMyDay: 'Mijn dag',
        backToOrder: 'Keuring',
        backToOrders: 'Keuringen',
        /** De knop van de schil, links in de balk als de rail ingeklapt is. */
        appMenu: 'Menu',
        offline: 'Offline · lokaal bewaard',
        online: 'Alles gesynchroniseerd',
        /** De stip in de balk: de naam zegt de hele status. */
        syncStatus: (status: string) => `Synchronisatie: ${status}`,
        syncTitle: 'Synchronisatie',
        syncNow: 'Nu versturen',
        /** In het venster onder de stip: waarom er geen knop Opslaan is. */
        autoSaved: 'Elk antwoord staat meteen op dit apparaat.',
        counterLabel: 'Beantwoord',
        /** Boven de titel: het hoeveelste hoofdonderdeel dit is. */
        partPosition: (index: number, total: number) => `Onderdeel ${index} van ${total}`,
        /** De toegankelijke naam van de teller in de balk. */
        counter: (answered: number, visible: number) =>
            `${answered} van ${visible} ${visible === 1 ? 'vraag' : 'vragen'} beantwoord`,
        nextIncomplete: 'Naar de eerste open vraag',
        complete: 'Afronden',
        progressLabel: 'Voortgang opname',
    },
    menu: {
        /** De knop met de drie puntjes in de balk. */
        open: 'Meer',
        title: 'Opname',
        close: 'Menu sluiten',
        progress: (answered: number, visible: number, percentage: number) =>
            `${answered} van ${visible} ${visible === 1 ? 'vraag' : 'vragen'} beantwoord · ${percentage}%`,
        allAnswered: 'Alles is beantwoord.',
    },
    panels: {
        topics: 'Onderwerpen',
        findings: 'Bevindingen & kosten',
        findingsShort: 'Bevindingen',
    },
    tree: {
        title: 'Onderwerpen',
        empty: 'Deze vragenlijst heeft nog geen onderdelen.',
        /** "3 van 7 vragen beantwoord" - de toegankelijke naam van de teller. */
        itemCount: (done: number, total: number) =>
            `${done} van ${total} ${total === 1 ? 'vraag' : 'vragen'} beantwoord`,
        phase: {
            klaar: 'klaar',
            bezig: 'bezig',
            open: 'open',
        },
    },
    item: {
        /** Boven de stroom: hoe ver dit hoofdonderdeel is, en wat de wandeling hier niet vraagt. */
        meta: (answered: number, applicable: number, skipped: number) =>
            [
                `${answered} van ${applicable} ${applicable === 1 ? 'vraag' : 'vragen'} beantwoord`,
                skipped > 0 ? `${skipped} vervallen` : null,
            ]
                .filter(Boolean)
                .join(' · '),
        sweep: (open: number) => `Geen gebreken hier (${open})`,
        previous: 'Vorig onderdeel',
        /** De kolom met de vragen: veeg opzij voor het vorige of volgende onderdeel. */
        column: 'Vragen van dit onderdeel',
        next: 'Volgend onderdeel →',
        empty: 'Geen vragen van toepassing op dit onderdeel.',
        /** De teller op de tussenkop van een subonderdeel. */
        subPartCount: (answered: number, visible: number) =>
            `${answered} van ${visible} ${visible === 1 ? 'vraag' : 'vragen'} beantwoord`,
    },
    shared: {
        /** Op de kaart van een gedeelde vraag in een later onderdeel. */
        note: (place: string) => `Deze vraag hoort bij ${place}. Je antwoord geldt ook daar.`,
        done: 'Klaar',
    },
    question: {
        photoRequired: 'Foto verplicht',
        /** Boven de antwoorden van de bouwperiodevraag: een hint, geen antwoord. */
        buildYearHint: (year: number) => `Bouwjaar volgens Kadaster: ${year}`,
        viewPhoto: (caption: string) => (caption ? `Foto ${caption} bekijken` : 'Foto bekijken'),
        closePhoto: 'Sluiten',
        photo: (count: number) => `Foto (${count})`,
        photoCount: (count: number) => `${count} ${count === 1 ? 'foto' : "foto's"}`,
        upload: 'Uploaden',
        photoHint: 'Gekoppeld aan dit onderdeel en aan het gekozen gebrek.',
        photoAlt: (caption: string) => `Foto ${caption}`,
        removePhoto: 'Foto verwijderen',
        keepPhoto: 'Annuleren',
        dropHere: "Laat los om foto's toe te voegen",
        photoPending: 'Nog niet geüpload',
        photoFailed: 'Niet geüpload',
        photoConverting: (caption: string) => `${caption} · wordt omgezet`.trim(),
        /** Onder het raster: hoeveel foto's nog onderweg zijn of bleven liggen. */
        photoWaiting: (count: number) =>
            count === 1
                ? 'Eén foto wacht op verzending.'
                : `${count} foto's wachten op verzending.`,
        photoRejected: (count: number) =>
            count === 1
                ? 'Eén foto is niet geüpload. De foto staat nog op dit apparaat.'
                : `${count} foto's zijn niet geüpload. Ze staan nog op dit apparaat.`,
        photoRetry: 'Opnieuw proberen',
        photoRetryAll: (count: number) =>
            count === 1 ? 'Opnieuw proberen' : `Alle ${count} opnieuw proberen`,
        /** Een bestand dat het apparaat niet als foto kon aannemen: per bestand, met wat je eraan doet. */
        photoNotAdded: (count: number) =>
            count === 1
                ? 'Eén bestand is niet toegevoegd'
                : `${count} bestanden zijn niet toegevoegd`,
        photoRefused: {
            empty: (fileName: string) =>
                `${fileName} is leeg op dit apparaat. Staat de foto alleen in iCloud, open hem dan eerst in Foto's en voeg hem daarna opnieuw toe.`,
            unreadable: (fileName: string) =>
                `${fileName} kon niet gelezen worden. Maak de foto opnieuw met de camera en voeg hem opnieuw toe.`,
            notAPhoto: (fileName: string) =>
                `${fileName} is geen foto. Voeg een jpeg, png, webp of een iPhone-foto (HEIC) toe.`,
        },
        photoNoticeClose: 'Melding sluiten',
        numberLabel: 'Getal',
        textLabel: 'Antwoord',
        textItemsHint: 'Eén punt per regel.',
    },
    multi: {
        label: (name: string) => `Hoeveel ${name.toLowerCase()}?`,
        decrease: 'Eén minder',
        increase: 'Eén meer',
        hint: 'Een lager aantal wist niets: de antwoorden komen terug.',
    },
    load: {
        noEnvironment: 'Kies eerst een omgeving',
        noEnvironmentBody: 'Log in of kies het bedrijf waarvoor je deze opname doet.',
        impersonating: 'De opname is niet beschikbaar tijdens het meekijken',
        impersonatingBody:
            'Een opname staat alleen op het apparaat van wie hem uitvoert, en tijdens het meekijken komt daar niets van op dit apparaat. Keuringen en rapporten bekijk je via Keuringen.',
        notFound: 'Deze opname staat niet op dit apparaat',
        notFoundBody:
            'Een opname komt op dit apparaat zodra de keuring aan je is toegewezen en er verbinding is geweest. Een ingediende opname verdwijnt bij de volgende synchronisatie.',
        /** Net gestart: de opname en de vragenlijst komen nu binnen. Geen fout, dus geen uitleg over toewijzen. */
        preparing: 'De opname wordt klaargezet',
        preparingBody:
            'De keuring is gestart. De opname en de vragenlijst komen nu naar dit apparaat; zodra ze er zijn, opent de opname vanzelf.',
        noQuestionnaire: 'Bij deze dienst hoort geen vragenlijst',
        noQuestionnaireBody:
            'Voor deze dienst is geen vragenlijst gepubliceerd, dus hier valt niets op te nemen. Is dat niet de bedoeling, vraag dan de eigenaar van je bedrijf om een vragenlijst voor dit type keuring.',
        packagePending: 'De vragenlijst wordt opgehaald',
        packagePendingBody:
            'De vragenlijst staat nog niet op dit apparaat. Zodra er verbinding is, komt hij binnen; daarna werkt de opname ook offline.',
        packageInvalid: 'De vragenlijst kon niet worden gelezen',
        packageInvalidBody:
            'De vragenlijst op dit apparaat is onvolledig. Bij de volgende synchronisatie wordt hij opnieuw opgehaald.',
        loading: 'Opname laden',
        backToMyDay: 'Terug naar Mijn dag',
        backToOrder: 'Terug naar de keuring',
        backToOrders: 'Terug naar Keuringen',
    },
    completion: {
        eyebrow: 'Afronden',
        title: 'Opname afronden',
        submittedTitle: 'Opname ingediend',
        notCompleted: 'Nog niet afgerond.',
        completedAt: (moment: string) =>
            `Afgerond op ${moment}. Je kunt nog antwoorden aanpassen tot je indient.`,
        submittedAt: (moment: string) => `Ingediend op ${moment}.`,
        answered: (answered: number, visible: number) =>
            `${answered} van ${visible} ${visible === 1 ? 'vraag' : 'vragen'} beantwoord.`,
        photosMissing: (count: number) =>
            count === 1
                ? 'Er ontbreekt 1 verplichte foto.'
                : `Er ontbreken ${count} verplichte foto's.`,
        amountsMissing: (count: number) =>
            count === 1
                ? 'Bij 1 antwoord moet je het bedrag nog invullen.'
                : `Bij ${count} antwoorden moet je het bedrag nog invullen.`,
        amountsTitle: 'Bedrag nog invullen',
        amountsExplanation:
            'Deze keuzes hebben geen vast bedrag. Je kunt toch indienen; zonder bedrag telt er geen geld mee.',
        openTitle: 'Nog open',
        nothingOpen: 'Alles is beantwoord en elke verplichte foto staat erbij.',
        nodeOpen: (unanswered: number, photos: number) =>
            [
                unanswered > 0
                    ? `${unanswered} open ${unanswered === 1 ? 'vraag' : 'vragen'}`
                    : null,
                photos > 0
                    ? `${photos} ${photos === 1 ? 'foto ontbreekt' : "foto's ontbreken"}`
                    : null,
            ]
                .filter(Boolean)
                .join(' · '),
        complete: 'Opname afronden',
        completeExplanation:
            'Afronden kan ook zonder verbinding en blokkeert nooit. Wat nog open staat, komt niet in het rapport.',
        openStaysOut: (count: number) =>
            count === 1
                ? 'De open vraag komt niet in het rapport.'
                : `De ${count} open vragen komen niet in het rapport.`,
        photosOnTheWay: (count: number) =>
            count === 1
                ? 'Eén verplichte foto is nog onderweg naar de server. Je kunt toch indienen: hij komt daarna alsnog in het rapport.'
                : `${count} verplichte foto's zijn nog onderweg naar de server. Je kunt toch indienen: ze komen daarna alsnog in het rapport.`,
        submit: 'Indienen',
        submitting: 'Bezig met indienen',
        submitExplanation:
            'Na indienen is de opname niet meer te wijzigen en gaat hij naar de eigenaar voor het rapport.',
        submitNeedsConnection: 'Indienen kan alleen met verbinding.',
        submitNeedsSync: (operations: number) =>
            `Eerst versturen: ${operations} ${operations === 1 ? 'wijziging' : 'wijzigingen'}. Dat gaat vanzelf; met "Nu versturen" meteen.`,
        submitWithPhotosOnTheWay: (photos: number) =>
            `Na indienen is de opname niet meer te wijzigen. ${photos === 1 ? 'De foto die nog onderweg is, gaat' : `De ${photos} foto's die nog onderweg zijn, gaan`} daarna gewoon mee; het concept wordt dan bijgewerkt.`,
        syncNow: 'Nu versturen',
        photoQueueTitle: (count: number) =>
            count === 1
                ? 'Eén foto gaat nog naar de server'
                : `${count} foto's gaan nog naar de server`,
        photoQueueExplanation:
            'Ze gaan vanzelf, ook als je dit scherm verlaat. Laat de app open staan tot ze er zijn.',
        photoStatus: {
            waitingForAnswer: 'Wacht tot het antwoord is verstuurd.',
            withoutAnswer:
                'Deze vraag heeft geen antwoord meer, dus de foto kan nergens heen. Beantwoord de vraag of sla de foto over.',
            queued: 'Staat klaar om te versturen.',
            sending: 'Wordt verstuurd.',
            retrying: (error: string, time: string) =>
                `Lukt nog niet: ${sentence(error)} Volgende poging om ${time}.`,
            refused: (error: string | null) =>
                `Niet verstuurd: ${error ? sentence(error) : 'de server nam de foto niet aan.'}`,
        },
        photoRetry: 'Opnieuw proberen',
        photoSkip: 'Overslaan',
        photoSkipLabel: (caption: string) => `Foto ${caption} overslaan; hij gaat niet mee`,
        photoRetryLabel: (caption: string) => `Foto ${caption} opnieuw versturen`,
        backToSurvey: 'Terug naar de vragen',
        completeFailed: 'Afronden is niet lokaal opgeslagen. Probeer het opnieuw.',
        submitOffline:
            'Indienen lukte niet: er is geen verbinding. Probeer het opnieuw zodra er bereik is.',
        submitForbidden: 'Je mag deze opname niet indienen.',
        submitFailed: 'Indienen lukte niet. Probeer het opnieuw.',
    },
    manual: {
        add: 'Bevinding toevoegen',
        newTitle: 'Nieuwe bevinding',
        editTitle: 'Bevinding wijzigen',
        place: 'Onderdeel',
        description: 'Omschrijving',
        location: 'Locatie',
        locationPlaceholder: 'Waar zit het? Bijvoorbeeld: rechterzijde, tweede raam',
        photos: "Foto's",
        photosEmpty: "Nog geen foto's. Maak er een, upload er een of sleep ze op dit formulier.",
        photoCaption: 'bevinding',
        descriptionPlaceholder: 'Beschrijf wat je ziet, in de taal van de koper.',
        severity: 'Soort',
        save: 'Opslaan',
        cancel: 'Annuleren',
        remove: 'Bevinding verwijderen',
        keep: 'Houden',
        confirmRemove: 'Deze bevinding verwijderen?',
        marker: 'Handmatig',
        edit: (label: string) => `Bevinding wijzigen: ${label}`,
    },
    locked: {
        title: (moment: string | null) =>
            moment ? `Ingediend op ${moment}` : 'Deze opname is ingediend',
        body: 'De opname is niet meer te wijzigen. Moet er iets anders, vraag de eigenaar dan om de opname te heropenen.',
        toReport: 'Naar rapport',
    },
    conflicts: {
        title: (count: number) =>
            count === 1
                ? 'Eén wijziging is niet overgenomen'
                : `${count} wijzigingen zijn niet overgenomen`,
        replaced: (subject: string) =>
            `${subject} is intussen op een ander apparaat gewijzigd. Die versie staat nu hier; kijk even of hij klopt.`,
        rejected: (subject: string, reason: string) => `${subject} is niet opgeslagen. ${reason}`,
        photo: (fileName: string, reason: string) => `Foto ${fileName} is niet geüpload. ${reason}`,
        photoDelete: (fileName: string, reason: string) =>
            `Foto ${fileName} is niet verwijderd en staat er weer. ${reason}`,
        subjectAnswer: (label: string) => `“${label}”`,
        subjectCount: (name: string) => `Aantal ${name.toLowerCase()}`,
        subjectOther: 'Een wijziging',
        noReason: 'Vul het opnieuw in. Lukt dat niet, neem dan contact op met de eigenaar.',
        /** Alleen meldingen: alles is opgeslagen, maar de vragenlijst werd intussen vernieuwd. */
        noticeTitle: 'De vragenlijst is bijgewerkt',
        noticeFallback: 'Een wijziging staat op een andere plek in de nieuwe vragenlijst.',
        acknowledge: 'Gezien',
    },
    /** Een keuze zonder vast bedrag: de inspecteur vult het zelf in, direct onder de keuze. */
    amountPrompt: {
        title: 'Vul het bedrag in',
        filled: 'Bedrag zelf ingevuld',
        explanation:
            'Deze keuze heeft geen vast bedrag. Zolang het leeg is, telt er geen geld mee in het rapport.',
        label: (urgency: string) => `Herstelkosten · ${urgency.toLowerCase()}`,
        placeholder: '€',
        ariaLabel: (question: string) => `Bedrag bij ${question}`,
    },
    detail: {
        title: 'Bevinding voor het rapport',
        notePlaceholder: 'Beschrijf wat je ziet, in de taal van de koper.',
        voiceIdle: 'Spreek bevinding in',
        voiceRecording: 'Opnemen… tik om te stoppen',
        voiceUnavailable: 'Spraak werkt niet op dit apparaat — typ de bevinding.',
        /** Waar het gebrek zit: een handvol woorden, optioneel, met dezelfde spraakknop. */
        location: 'Locatie',
        locationPlaceholder: 'Waar zit het? Bijvoorbeeld: rechterzijde, tweede raam',
        locationVoiceIdle: 'Spreek locatie in',
        locationVoiceUnavailable: 'Spraak werkt niet op dit apparaat — typ de locatie.',
        /** De ene regel die zegt dat de bedragen vanzelf komen; verder blijven ze dicht. */
        costsTitle: 'Kosten',
        costsAuto: 'uit de vragenlijst',
        costsOwn: 'zelf ingevuld',
        costsNone: 'Nog geen bedrag',
        costsOpen: 'Aanpassen',
        costsClose: 'Klaar',
        costsOpenLabel: (question: string) => `Kosten aanpassen bij ${question}`,
        costsExplanation:
            'Deze bedragen komen uit de vragenlijst. Schat je het anders in, pas ze hier aan.',
        repairCost: 'Herstelkosten',
        urgency: 'Urgentie',
        nhg: 'NHG-bijlage',
        noChoice: 'Nog niet gekozen',
    },
    findings: {
        title: 'Bevindingen',
        summary: (findings: number, answered: number, visible: number) =>
            `${findings} ${findings === 1 ? 'bevinding' : 'bevindingen'} vastgelegd · ${answered} van ${visible} ${visible === 1 ? 'vraag' : 'vragen'} beantwoord`,
        empty: 'Nog geen bevindingen. Een schoon antwoord levert geen werk op.',
        noPlace: 'Onbekend onderdeel',
        costDirect: 'Direct',
        costTerm: 'Op termijn',
        costImprovement: 'Verbetering',
        costTotal: 'Totaal',
        notAskedTitle: 'Niet gesteld',
        notAsked: (count: number) =>
            `${count} ${count === 1 ? 'vraag blijft' : 'vragen blijven'} dicht: ${count === 1 ? 'hij is' : 'ze zijn'} op dit object niet van toepassing.`,
    },
} as const;

/**
 * Wat er misging bij het lokaal opslaan, en wat de inspecteur eraan kan doen.
 * Geen technische termen, geen uitroeptekens: de opname gaat gewoon door.
 */
export const surveyErrors = {
    answer: 'Dit antwoord is niet lokaal opgeslagen. Tik het opnieuw aan.',
    sweep: '"Geen gebreken hier" is niet opgeslagen; de vragen staan nog open. Tik het opnieuw aan.',
    note: 'De bevindingtekst is niet opgeslagen. Tik in het veld en verlaat het opnieuw.',
    location: 'De locatie is niet opgeslagen. Tik in het veld en verlaat het opnieuw.',
    finding: 'Kosten, urgentie of NHG zijn niet opgeslagen. Probeer het opnieuw.',
    photo: 'De foto is niet lokaal opgeslagen. Maak hem opnieuw.',
    photoRemove: 'De foto is niet verwijderd. Probeer het opnieuw.',
    manualFinding: 'De bevinding is niet lokaal opgeslagen. Probeer het opnieuw.',
    locked: 'Deze opname is ingediend en niet meer te wijzigen.',
    storageFull:
        "Dit apparaat heeft geen ruimte meer om op te slaan. Maak ruimte vrij, bijvoorbeeld door oude foto's of apps te verwijderen, en probeer het dan opnieuw.",
    storageLost:
        'Opslaan op dit apparaat lukt nu niet. Sluit de app helemaal af en open hem opnieuw.',
    dismiss: 'Melding sluiten',
    title: 'Lokaal opslaan mislukte',
    keep: 'Je eerdere antwoorden staan er nog.',
    boundaryTitle: 'Dit onderdeel liep vast',
    boundaryBody:
        'Ga verder met een ander onderdeel of probeer het opnieuw. De opname blijft lokaal bewaard.',
    retry: 'Opnieuw proberen',
} as const;

export const energyCopy = {
    header: {
        eyebrow: (orderNumber: string) => `NTA 8800 · OPNAME BASISMETHODE · ${orderNumber}`,
        unknownAddress: 'Onbekend adres',
        localOnly: 'Alleen op dit apparaat',
    },
    notice: {
        title: 'Deze gegevens blijven op dit apparaat',
        body: 'Synchroniseren kan nog niet voor de energielabelopname. Het label zelf bereken en registreer je in je eigen rekenprogramma en EP-online; de app berekent geen label.',
    },
    locked: 'Deze opname is ingediend. De gegevens zijn alleen nog te lezen.',
    general: {
        title: 'Algemeen',
        year: 'Bouwjaar',
        usableArea: 'Gebruiksoppervlak',
        lossArea: 'Verliesoppervlak',
    },
    shell: {
        title: 'Bouwkundige schil',
        description: 'Rc/U-waarden · forfaitair waar niet vastgesteld',
        columns: {
            part: 'Bouwdeel',
            construction: 'Constructie',
            value: 'Waarde',
            source: 'Bron',
        },
        parts: {
            dak: 'Dak',
            gevel: 'Gevel',
            vloer: 'Vloer',
            beglazing: 'Beglazing',
        },
        field: (part: string, column: string) => `${column} ${part.toLowerCase()}`,
    },
    installations: {
        title: 'Installaties',
        heating: 'Verwarming',
        ventilation: 'Ventilatie',
        solar: 'Zonnepanelen',
        water: 'Tapwater',
    },
    noChoice: 'Nog niet gekozen',
} as const;

/** Een foutmelding als zin: precies één punt aan het eind. */
function sentence(text: string): string {
    return `${text.trim().replace(/[.\s]+$/, '')}.`;
}
